import React from "react";
import AsyncImage from "@appComponents/AsyncImage/AsyncImage";
import noPhotoCompany from "@images/no_photo_company.png";
import PDFIcon from "@assets/images/pdf-icon.svg";
import { MAIN_CURRENCY_LABEL } from "@bundles/consts";
import Big from "big.js";
import { IMyTradeTransaction } from "./IMyTradeTransaction";
import moment from "moment";
import ETradeTypes from "@market/enums/TradeTypes";
import { Button } from "@appComponents/Button";
import classNames from "classnames";
import ChevronDownIcon from "../../../../icons/chevron-down-black.svg";
import { formatNumberWithCommas } from "@features/utils/functions";

const typeLabels = {
  [ETradeTypes.buy]: "Buy",
  [ETradeTypes.sell]: "Sell",
};

const big = Big();

const calculateTotalAmount = (trade: IMyTradeTransaction) => {
  if (trade.type === ETradeTypes.buy) {
    return big(Number(trade.price_per_share ?? 0))
      .times(Number(trade.number_of_shares ?? 0))
      .plus(Number(trade.fee_amount ?? 0));
  }

  if (trade.type === ETradeTypes.sell) {
    return big(Number(trade.price_per_share ?? 0))
      .times(Number(trade.number_of_shares ?? 0))
      .minus(Number(trade.fee_amount ?? 0));
  }

  return 0;
};

type GetMyTradeColumnsArgs = {
  getAgreement: (tradeId: number) => void;
  isMobile: boolean;
  handleExpandToggle: (record: IMyTradeTransaction) => void;
  expandedRowKeys: React.Key[];
};

export const getMyTradeColumns = ({
  getAgreement,
  isMobile,
  handleExpandToggle,
  expandedRowKeys,
}: GetMyTradeColumnsArgs) =>
  isMobile
    ? [
        {
          title: "NAME",
          dataIndex: "spv_name",
          key: "spv_name",
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage
                  image={trade.company_avatar.url}
                  fallback={noPhotoCompany}
                >
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <span>{trade.spv_name}</span>
              </div>
            );
          },
        },
        {
          title: "",
          dataIndex: "action",
          key: "action",
          width: 10,
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <Button isSmall onClick={() => handleExpandToggle(trade)}>
                <span
                  className={classNames({
                    "rotate-180": expandedRowKeys.includes(trade.id),
                  })}
                >
                  <ChevronDownIcon />
                </span>
              </Button>
            );
          },
        },
      ]
    : [
        {
          title: "NAME",
          dataIndex: "spv_name",
          key: "spv_name",
          sorter: (a, b) => a.spv_name.localeCompare(b.spv_name),
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <div className="flex items-center gap-2">
                <AsyncImage
                  image={trade.company_avatar.url}
                  fallback={noPhotoCompany}
                >
                  <img alt="logo" className="h-[36px] w-[36px] rounded-[5px]" />
                </AsyncImage>

                <span>{trade.spv_name}</span>
              </div>
            );
          },
        },
        {
          title: "DATE",
          dataIndex: "created_at",
          key: "created_at",
          sorter: (a, b) =>
            moment(a.created_at).valueOf() - moment(b.created_at).valueOf(),
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <div>
                <div>{moment(trade.created_at).format("DD MMM, YYYY")}</div>
                <div className="text-xs">
                  {moment(trade.created_at).format("h:mm A")}
                </div>
              </div>
            );
          },
        },
        {
          title: "TYPE",
          dataIndex: "type",
          key: "type",
          sorter: (a, b) => a.type.localeCompare(b.type),
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <div
                className={classNames({
                  "text-[#2D9C5E]": trade.type === ETradeTypes.buy,
                  "text-[#E02020]": trade.type === ETradeTypes.sell,
                })}
              >
                {typeLabels[trade.type]}
              </div>
            );
          },
        },
        {
          title: "STATUS",
          dataIndex: "status",
          key: "status",
          sorter: (a, b) => a.status.localeCompare(b.status),
          render: (text: string) =>
            text.charAt(0).toUpperCase() + text.slice(1),
        },
        {
          title: "SHARE PRICE",
          dataIndex: "price_per_share",
          key: "price_per_share",
          sorter: (a, b) =>
            Number(a.price_per_share ?? 0) - Number(b.price_per_share ?? 0),
          render: (_, trade: IMyTradeTransaction) => {
            if (!trade.price_per_share) {
              return "-";
            }

            return (
              <div>
                {`${formatNumberWithCommas(
                  Number(trade.price_per_share ?? 0)
                )} ${MAIN_CURRENCY_LABEL}`}
              </div>
            );
          },
        },
        {
          title: "SHARE AMOUNT",
          dataIndex: "number_of_shares",
          key: "number_of_shares",
          sorter: (a, b) =>
            Number(a.number_of_shares ?? 0) - Number(b.number_of_shares ?? 0),
          render: (text: string) => {
            return formatNumberWithCommas(Number(text ?? 0), 0);
          },
        },
        {
          title: "FEE",
          dataIndex: "fee_amount",
          key: "fee_amount",
          sorter: (a, b) =>
            Number(a.fee_amount ?? 0) - Number(b.fee_amount ?? 0),
          render: (_, trade: IMyTradeTransaction) => {
            if (!trade.fee_amount) {
              return "-";
            }

            return (
              <div>{`${formatNumberWithCommas(
                Number(trade.fee_amount ?? 0),
                4
              )} ${trade.fee_currency.toUpperCase()}`}</div>
            );
          },
        },
        {
          title: "TOTAL AMOUNT",
          dataIndex: "total_amount",
          key: "total_amount",
          sorter: (a, b) => calculateTotalAmount(a) - calculateTotalAmount(b),
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <div>
                <div>
                  {formatNumberWithCommas(calculateTotalAmount(trade), 4)}
                </div>
                <div className="text-xs">
                  <div>{`${
                    trade.type === ETradeTypes.buy ? "+" : "-"
                  } ${formatNumberWithCommas(
                    Number(trade.fee_amount ?? 0),
                    4
                  )}  ${trade.fee_currency.toUpperCase()}`}</div>
                </div>
              </div>
            );
          },
        },

        {
          title: "AGREEMENT",
          dataIndex: "agreement",
          key: "agreement",
          render: (_, trade: IMyTradeTransaction) => {
            return (
              <div
                className="cursor-pointer flex justify-center"
                onClick={() => getAgreement(trade.id)}
              >
                <PDFIcon />
              </div>
            );
          },
        },
      ];
